/*
  DogPure · ficha completa (plantilla product.mobility-completa).
  <dp-packs>: el paquete elegido fija la cantidad y la propiedad «Bundle» del formulario nativo; aplica el código de
    descuento del paquete igual que la ficha publicada (/discount/CÓDIGO) al elegirlo y al agregarlo al carrito.
  <dp-tabs>: pestañas de citas (veterinarios). Sin JavaScript se ven todas las citas.
  <dp-reviews>: paginación, tira de fotos y ampliación de las reseñas. Sin JavaScript se ven todas.
  <dp-pdp-sticky>: botón fijo del celular; lleva al selector de paquetes (o a la compra, si no hay paquetes)
    y muestra la selección.
  Selector de variante (productos sin paquetes): actualiza precio, precio tachado y botón con los datos de cada
    variante pintados por Shopify; las agotadas no se pueden elegir.
  Galería (dp-gallery--rail): amplía al tocar la imagen, salvo si fue un deslizamiento, y mantiene visible la
    miniatura activa dentro de su fila.
*/

// Varias secciones cargan este archivo: la función se define una sola vez
window.dpApplyDiscount ||= (code) => {
  if (!code) return;
  fetch(`/discount/${encodeURIComponent(code)}`, { credentials: 'same-origin', redirect: 'manual' }).catch(() => {});
};

if (!customElements.get('dp-packs')) {
  customElements.define(
    'dp-packs',
    class DpPacks extends HTMLElement {
      connectedCallback() {
        this.form = document.getElementById(this.dataset.form);
        this.inputs = [...this.querySelectorAll('input[type="radio"]')];
        if (!this.form || this.inputs.length === 0) return;
        this.quantity = this.form.querySelector('[data-pack-quantity]');
        this.property = this.form.querySelector('[data-pack-property]');
        this.inputs.forEach((input) => input.addEventListener('change', () => this.update(true)));
        this.form.addEventListener('submit', () => window.dpApplyDiscount(this.current?.dataset.discountCode));
        this.update(false);
      }

      get current() {
        return this.inputs.find((input) => input.checked) || this.inputs[0];
      }

      update(fromUser) {
        const input = this.current;
        if (!input) return;
        if (this.quantity) this.quantity.value = input.value;
        if (this.property) this.property.value = input.dataset.label;
        this.dataset.label = input.dataset.label;
        this.dataset.price = input.dataset.price;
        // Texto del botón con el precio del paquete elegido («Añadir paquete · [price]»)
        const button = this.form.querySelector('[data-buy-button]');
        const label = button?.querySelector('[data-label]');
        const template = button?.dataset.addLabel || '';
        if (label && !button.disabled && template.includes('[price]')) {
          label.textContent = template.replace('[price]', input.dataset.price);
        }
        document.dispatchEvent(
          new CustomEvent('dp:pack-change', { detail: { label: input.dataset.label, price: input.dataset.price } })
        );
        if (fromUser) {
          const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1));
          idle(() => window.dpApplyDiscount(input.dataset.discountCode));
        }
      }
    }
  );
}

if (!customElements.get('dp-tabs')) {
  customElements.define(
    'dp-tabs',
    class DpTabs extends HTMLElement {
      connectedCallback() {
        this.tabs = [...this.querySelectorAll('[data-tab]')];
        this.panels = [...this.querySelectorAll('[data-panel]')];
        if (this.tabs.length < 2) return;
        this.tabs.forEach((tab, i) => {
          tab.addEventListener('click', () => this.select(i));
          tab.addEventListener('keydown', (event) => {
            const keys = { ArrowRight: 1, ArrowLeft: -1 };
            if (!(event.key in keys)) return;
            event.preventDefault();
            const next = (i + keys[event.key] + this.tabs.length) % this.tabs.length;
            this.select(next);
            this.tabs[next].focus();
          });
        });
        this.select(0);
      }

      select(index) {
        this.tabs.forEach((tab, i) => {
          tab.setAttribute('aria-selected', String(i === index));
          tab.tabIndex = i === index ? 0 : -1;
        });
        this.panels.forEach((panel, i) => (panel.hidden = i !== index));
      }
    }
  );
}

if (!customElements.get('dp-reviews')) {
  customElements.define(
    'dp-reviews',
    class DpReviews extends HTMLElement {
      connectedCallback() {
        this.cards = [...this.querySelectorAll('[data-review]')];
        this.perPage = Number(this.dataset.perPage) || 8;
        this.pages = Math.ceil(this.cards.length / this.perPage);
        this.nav = this.querySelector('[data-pagination]');
        this.dialog = this.querySelector('dialog');
        this.zooms = [...this.querySelectorAll('[data-review-zoom]')];
        this.page = 1;

        this.buildPhotos();
        if (this.pages > 1) {
          this.nav.hidden = false;
          this.nav.addEventListener('click', (event) => {
            const button = event.target.closest('button[data-page]');
            if (!button || button.disabled) return;
            this.show(Number(button.dataset.page), true);
          });
        }
        this.show(1, false);

        this.zooms.forEach((button, i) => button.addEventListener('click', () => this.open(i)));
        if (this.dialog) {
          this.dialog.querySelector('[data-close]')?.addEventListener('click', () => this.dialog.close());
          this.dialog.querySelector('[data-dialog-prev]')?.addEventListener('click', () => this.open(this.index - 1));
          this.dialog.querySelector('[data-dialog-next]')?.addEventListener('click', () => this.open(this.index + 1));
          this.dialog.addEventListener('click', (event) => {
            if (event.target === this.dialog) this.dialog.close();
          });
          this.dialog.addEventListener('close', () => this.zooms[this.index]?.focus());
        }
      }

      // Tira de fotos del resumen: una miniatura por reseña con foto; abre la ampliación de esa reseña
      buildPhotos() {
        const list = this.querySelector('[data-photos]');
        if (!list || this.zooms.length === 0) return;
        this.zooms.slice(0, 8).forEach((zoom, i) => {
          const item = document.createElement('li');
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'dp-reviews__photo';
          button.setAttribute('aria-label', zoom.getAttribute('aria-label'));
          button.setAttribute('aria-haspopup', 'dialog');
          const img = document.createElement('img');
          img.src = zoom.dataset.reviewThumb;
          img.alt = '';
          img.width = 80;
          img.height = 80;
          img.loading = 'lazy';
          button.append(img);
          button.addEventListener('click', () => this.open(i));
          item.append(button);
          list.append(item);
        });
        list.hidden = false;
      }

      show(page, scroll) {
        this.page = Math.max(1, Math.min(this.pages || 1, page));
        const start = (this.page - 1) * this.perPage;
        this.cards.forEach((card, i) => (card.hidden = i < start || i >= start + this.perPage));
        if (this.pages > 1) this.renderNav();
        if (scroll) {
          const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          this.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
          this.cards[start]?.setAttribute('tabindex', '-1');
          this.cards[start]?.focus({ preventScroll: true });
        }
      }

      // Ventana de 5 números como en la ficha publicada, más «anterior» y «siguiente»
      renderNav() {
        const win = Math.min(5, this.pages);
        const from = Math.min(Math.max(this.page - Math.floor(win / 2), 1), this.pages - win + 1);
        const button = (label, page, opts = {}) =>
          `<button type="button" class="dp-reviews__page${opts.arrow ? ' dp-reviews__page--arrow' : ''}" data-page="${page}"${
            opts.current ? ' aria-current="page"' : ''
          }${opts.disabled ? ' disabled' : ''}${opts.aria ? ` aria-label="${opts.aria}"` : ''}>${label}</button>`;
        let html = button('‹', this.page - 1, { arrow: true, disabled: this.page === 1, aria: this.dataset.labelPrev || 'Página anterior' });
        for (let p = from; p < from + win; p++) {
          html += button(p, p, { current: p === this.page, aria: (this.dataset.labelPage || 'Página [page]').replace('[page]', p) });
        }
        html += button('›', this.page + 1, { arrow: true, disabled: this.page === this.pages, aria: this.dataset.labelNext || 'Página siguiente' });
        this.nav.innerHTML = html;
      }

      open(i) {
        if (!this.dialog || this.zooms.length === 0) return;
        this.index = (i + this.zooms.length) % this.zooms.length;
        const zoom = this.zooms[this.index];
        const card = zoom.closest('[data-review]');
        this.dialog.querySelector('img').src = zoom.dataset.reviewZoom;
        const info = this.dialog.querySelector('[data-info]');
        info.innerHTML = '';
        ['.dp-review__top', '.dp-review__author', '.dp-review__text', '.dp-review__reply'].forEach((selector) => {
          const el = card.querySelector(selector);
          if (el) info.append(el.cloneNode(true));
        });
        const multiple = this.zooms.length > 1;
        this.dialog.querySelectorAll('[data-dialog-prev], [data-dialog-next]').forEach((b) => (b.hidden = !multiple));
        if (!this.dialog.open) this.dialog.showModal();
        this.dialog.scrollTop = 0;
      }
    }
  );
}

if (!customElements.get('dp-pdp-sticky')) {
  customElements.define(
    'dp-pdp-sticky',
    class DpPdpSticky extends HTMLElement {
      connectedCallback() {
        this.target = document.getElementById(this.dataset.target) || document.getElementById('comprar');
        this.button = this.querySelector('[data-go]');
        this.summary = this.querySelector('[data-summary]');
        this.offset = Number(this.dataset.offset) || 0;
        if (!this.target || !this.button) return;

        // Zona de compra: el selector de paquetes y el botón principal
        this.buyZone = this.target.closest('.dp-pdp-buy') || this.target;
        this.zoneVisible = true;
        this.renderSummary(this.target.dataset.label, this.target.dataset.price);
        document.addEventListener('dp:pack-change', (event) => this.renderSummary(event.detail.label, event.detail.price));

        this.button.addEventListener('click', () => this.goToPacks());
        this.observer = new IntersectionObserver(([entry]) => {
          this.zoneVisible = entry.isIntersecting;
          this.update();
        });
        this.observer.observe(this.buyZone);
        this.onScroll = () => this.update();
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.update();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        window.removeEventListener('scroll', this.onScroll);
        document.body.style.paddingBottom = '';
      }

      renderSummary(label, price) {
        if (!this.summary || !label) return;
        this.summary.innerHTML = '';
        const name = document.createElement('span');
        name.textContent = label;
        const amount = document.createElement('strong');
        amount.textContent = price || '';
        this.summary.append(name, amount);
      }

      update() {
        const show = window.scrollY > this.offset && !this.zoneVisible;
        if (show === this.shown) return;
        this.shown = show;
        this.toggleAttribute('data-visible', show);
        this.setAttribute('aria-hidden', String(!show));
        this.button.tabIndex = show ? 0 : -1;
        // Reserva su altura al final de la página para no tapar el último contenido
        const active = getComputedStyle(this).display !== 'none';
        document.body.style.paddingBottom = active ? `${this.offsetHeight}px` : '';
      }

      goToPacks() {
        // El encabezado fijo reaparece al subir: se reserva siempre su altura, aunque ahora esté oculto
        const header = document.querySelector('.section-header .header-wrapper') || document.querySelector('.section-header');
        const headerHeight = header ? header.offsetHeight : 0;
        const top = this.target.getBoundingClientRect().top + window.scrollY - headerHeight - 16;
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({ top: Math.max(0, top), behavior: reduce ? 'auto' : 'smooth' });
        const checked =
          this.target.querySelector('input:checked') ||
          this.target.querySelector('select, input:not([type="hidden"])');
        checked?.focus({ preventScroll: true });
      }
    }
  );
}

if (!window.dpRailGallery) {
  window.dpRailGallery = true;
  const enhance = (gallery) => {
    if (gallery.dataset.railReady) return;
    gallery.dataset.railReady = '';
    const track = gallery.querySelector('[data-track]');
    const row = gallery.querySelector('[data-thumbs]');
    if (!track) return;

    // Tocar sin desplazar = ampliar. Un deslizamiento mueve el carrusel o cambia scrollLeft: no amplía.
    let start = null;
    track.addEventListener('pointerdown', (event) => {
      start = { x: event.clientX, y: event.clientY, left: track.scrollLeft, time: Date.now() };
    });
    track.addEventListener('click', (event) => {
      if (!start) return;
      const moved = Math.abs(event.clientX - start.x) > 8 || Math.abs(event.clientY - start.y) > 8;
      const scrolled = Math.abs(track.scrollLeft - start.left) > 2;
      const long = Date.now() - start.time > 600;
      start = null;
      if (moved || scrolled || long) return;
      gallery.openZoom?.();
    });

    // Miniatura activa siempre a la vista, desplazando solo la fila (nunca la página)
    if (!row) return;
    const follow = () => {
      const active = row.querySelector('[aria-current="true"]');
      if (!active) return;
      const item = active.parentElement;
      const pad = 4;
      const left = item.offsetLeft - row.offsetLeft;
      const right = left + item.offsetWidth;
      let target = null;
      if (left - pad < row.scrollLeft) target = left - pad;
      else if (right + pad > row.scrollLeft + row.clientWidth) target = right + pad - row.clientWidth;
      if (target === null) return;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      row.scrollTo({ left: Math.max(0, target), behavior: reduce ? 'auto' : 'smooth' });
    };
    new MutationObserver(follow).observe(row, { subtree: true, attributes: true, attributeFilter: ['aria-current'] });
  };
  const run = () => document.querySelectorAll('dp-gallery.dp-gallery--rail').forEach(enhance);
  customElements.whenDefined('dp-gallery').then(run);
  document.addEventListener('shopify:section:load', () => customElements.whenDefined('dp-gallery').then(run));
}

/* Productos sin paquetes: cambio de variante (precio y disponibilidad salen de las opciones pintadas por Shopify) */
if (!window.dpVariantSelect) {
  window.dpVariantSelect = true;
  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-variant-select]');
    if (!select) return;
    const option = select.selectedOptions[0];
    const buy = select.closest('.dp-pdp-buy');
    if (!option || !buy) return;
    const final = buy.querySelector('[data-price-final]');
    const compare = buy.querySelector('[data-price-compare]');
    const compareWrap = buy.querySelector('[data-price-compare-wrap]');
    if (final) final.textContent = option.dataset.price;
    if (compare) compare.textContent = option.dataset.compare;
    if (compareWrap) compareWrap.hidden = !option.dataset.compare;
    const button = buy.querySelector('[data-buy-button]');
    const label = button?.querySelector('[data-label]');
    const available = option.dataset.available === 'true';
    if (button) {
      button.disabled = !available;
      if (label) {
        const template = button.dataset.addLabel || '';
        label.textContent = available ? template.replace('[price]', option.dataset.price) : button.dataset.soldOutLabel;
      }
    }
    buy.dataset.price = option.dataset.price;
    document.dispatchEvent(
      new CustomEvent('dp:pack-change', { detail: { label: buy.dataset.label, price: option.dataset.price } })
    );
  });
}
